import "./CompanyWarehouseTransfers.css";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { toDataURL } from "qrcode";
import { useSearchParams } from "react-router-dom";

import { usePermissions } from "./PermissionContext";

const API_URL = "/api";

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("company_token")}`,
});

const today = () =>
  new Date().toISOString().slice(0, 10);

const emptyTransfer = () => ({
  source_warehouse_id: "",
  destination_warehouse_id: "",
  transfer_date: today(),
  notes: "",
});

const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item.msg).join(". ");
  }

  return "No fue posible completar la operación.";
};

const formatQuantity = (value) =>
  new Intl.NumberFormat("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return "";

  return new Date(`${value}T00:00:00`).toLocaleDateString(
    "es-CO"
  );
};

const getLogoUrl = (logo) => {
  if (!logo) return null;

  if (
    logo.startsWith("http://") ||
    logo.startsWith("https://") ||
    logo.startsWith("data:")
  ) {
    return logo;
  }

  return `${API_URL}${logo.startsWith("/") ? "" : "/"}${logo}`;
};

const formatDateTime = (value) => {
  if (!value) return "No disponible";

  return new Date(value).toLocaleString("es-CO", {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

function CompanyWarehouseTransfers() {
  const { can } = usePermissions();

  const [searchParams, setSearchParams] =
    useSearchParams();

  const canManageTransfers = can("inventory.manage");

  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [form, setForm] = useState(emptyTransfer);
  const [cart, setCart] = useState([]);
  const [selectedProductId, setSelectedProductId] =
    useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [processingTransferId, setProcessingTransferId] =
    useState(null);
  const [transferDetail, setTransferDetail] = useState(null);
  const [loadingTransferDetail, setLoadingTransferDetail] =
    useState(false);
  const [transferReceipt, setTransferReceipt] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [
        warehousesResponse,
        productsResponse,
        transfersResponse,
      ] = await Promise.all([
        axios.get(`${API_URL}/warehouses/me`, {
          headers: getHeaders(),
          params: {
            active_only: true,
          },
        }),
        axios.get(`${API_URL}/products/me`, {
          headers: getHeaders(),
        }),
        axios.get(`${API_URL}/warehouse-transfers/me`, {
          headers: getHeaders(),
        }),
      ]);

      setWarehouses(warehousesResponse.data);

      setProducts(
        productsResponse.data.filter(
          (product) => product.is_active
        )
      );

      setTransfers(transfersResponse.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const resetForm = () => {
    setForm(emptyTransfer());
    setCart([]);
    setSelectedProductId("");
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const addProduct = () => {
    if (!selectedProductId) {
      toast.error("Selecciona un producto.");
      return;
    }

    const product = products.find(
      (item) => Number(item.id) === Number(selectedProductId)
    );

    if (!product) {
      toast.error("Producto no encontrado.");
      return;
    }

    if (
      cart.some(
        (item) => Number(item.product_id) === Number(product.id)
      )
    ) {
      toast.error(
        "El producto ya está incluido en la transferencia."
      );
      return;
    }

    setCart((current) => [
      ...current,
      {
        product_id: product.id,
        sku: product.sku,
        name: product.name,
        quantity: 1,
      },
    ]);

    setSelectedProductId("");
  };

  const updateItemQuantity = (productId, value) => {
    setCart((current) =>
      current.map((item) =>
        item.product_id === productId
          ? {
            ...item,
            quantity: Number(value),
          }
          : item
      )
    );
  };

  const removeItem = (productId) => {
    setCart((current) =>
      current.filter(
        (item) => item.product_id !== productId
      )
    );
  };

  const totalQuantity = useMemo(
    () =>
      cart.reduce(
        (total, item) =>
          total + Number(item.quantity || 0),
        0
      ),
    [cart]
  );

  const saveTransfer = async (event) => {
    event.preventDefault();

    if (!canManageTransfers) {
      toast.error(
        "No tienes permiso para crear transferencias."
      );
      return;
    }

    if (!form.source_warehouse_id) {
      toast.error("Selecciona la bodega de origen.");
      return;
    }

    if (!form.destination_warehouse_id) {
      toast.error("Selecciona la bodega de destino.");
      return;
    }

    if (
      Number(form.source_warehouse_id) ===
      Number(form.destination_warehouse_id)
    ) {
      toast.error(
        "La bodega de origen y destino deben ser diferentes."
      );
      return;
    }

    if (cart.length === 0) {
      toast.error(
        "Agrega al menos un producto a la transferencia."
      );
      return;
    }

    if (
      cart.some((item) => Number(item.quantity) <= 0)
    ) {
      toast.error(
        "Todas las cantidades deben ser mayores que cero."
      );
      return;
    }

    setSaving(true);

    try {
      await axios.post(
        `${API_URL}/warehouse-transfers/me`,
        {
          source_warehouse_id: Number(
            form.source_warehouse_id
          ),
          destination_warehouse_id: Number(
            form.destination_warehouse_id
          ),
          transfer_date: form.transfer_date,
          notes: form.notes.trim() || null,
          items: cart.map((item) => ({
            product_id: item.product_id,
            quantity: Number(item.quantity),
          })),
        },
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        "Transferencia creada en borrador."
      );

      resetForm();
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const requestTransferAction = (transfer, action) => {
    setConfirmation({
      transfer,
      action,
    });
  };

  const closeConfirmation = () => {
    if (!processingTransferId) {
      setConfirmation(null);
    }
  };

  const confirmTransferAction = async () => {
    if (!confirmation) return;

    const { transfer, action } = confirmation;

    setProcessingTransferId(transfer.id);

    try {
      await axios.post(
        `${API_URL}/warehouse-transfers/me/${transfer.id}/${action === "complete" ? "complete" : "cancel"
        }`,
        {},
        {
          headers: getHeaders(),
        }
      );

      toast.success(
        action === "complete"
          ? "Transferencia completada. El inventario fue movido."
          : "Transferencia cancelada."
      );

      setConfirmation(null);
      await loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessingTransferId(null);
    }
  };

  const openTransferDetail = useCallback(async (transferId) => {
    setLoadingTransferDetail(true);
    setTransferDetail(null);
    setTransferReceipt(null);

    try {
      const response = await axios.get(
        `${API_URL}/warehouse-transfers/me/${transferId}/receipt`,
        {
          headers: getHeaders(),
        }
      );

      setTransferReceipt(response.data);
      setTransferDetail(response.data.transfer);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoadingTransferDetail(false);
    }
  }, []);

  useEffect(() => {
    const transferId = Number(
      searchParams.get("transfer_id")
    );

    if (
      !transferId ||
      loading ||
      !transfers.some(
        (transfer) => transfer.id === transferId
      )
    ) {
      return;
    }

    openTransferDetail(transferId);

    const nextParams = new URLSearchParams(searchParams);

    nextParams.delete("transfer_id");

    setSearchParams(nextParams, {
      replace: true,
    });
  }, [
    loading,
    openTransferDetail,
    searchParams,
    setSearchParams,
    transfers,
  ]);

  const statusLabel = (transferStatus) => {
    const labels = {
      draft: "Borrador",
      completed: "Completada",
      cancelled: "Cancelada",
    };

    return labels[transferStatus] || transferStatus;
  };

  const printTransferDetail = async () => {
    if (!transferReceipt?.transfer) {
      toast.error(
        "No fue posible obtener los datos del comprobante."
      );
      return;
    }

    const printWindow = window.open(
      "",
      "_blank",
      "width=900,height=700"
    );

    if (!printWindow) {
      toast.error(
        "El navegador bloqueó la ventana de impresión. Permite ventanas emergentes e inténtalo otra vez."
      );
      return;
    }

    const { company, transfer, created_by, completed_by } =
      transferReceipt;

    const verificationUrl =
      `${window.location.origin}` +
      `${window.location.pathname}` +
      `?transfer_id=${transfer.id}`;

    let qrDataUrl = null;

    try {
      qrDataUrl = await toDataURL(verificationUrl, {
        width: 160,
        margin: 1,
        errorCorrectionLevel: "M",
        color: {
          dark: "#1e293b",
          light: "#ffffff",
        },
      });
    } catch (error) {
      console.error("No fue posible generar el código QR.", error);
    }

    const totalQuantity = transfer.items.reduce(
      (total, item) =>
        total + Number(item.quantity || 0),
      0
    );

    const logoUrl = getLogoUrl(company.logo);

    const logoHtml = logoUrl
      ? `
      <img
        class="company-logo"
        src="${escapeHtml(logoUrl)}"
        alt="Logo de empresa"
      />
    `
      : `
      <div class="company-logo-fallback">
        ${escapeHtml(
        (company.company_name || "N")
          .slice(0, 2)
          .toUpperCase()
      )}
      </div>
    `;

    const companyContact = [
      company.address,
      company.city,
      company.phone || company.mobile,
      company.email,
    ]
      .filter(Boolean)
      .map(escapeHtml)
      .join(" · ");

    const itemRows = transfer.items
      .map(
        (item) => `
        <tr>
          <td>
            <strong>${escapeHtml(item.product_name)}</strong>
          </td>
          <td>${escapeHtml(item.product_sku)}</td>
          <td class="quantity">
            ${formatQuantity(item.quantity)}
          </td>
        </tr>
      `
      )
      .join("");

    printWindow.onload = () => {
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 250);
    };

    const qrHtml = qrDataUrl
      ? `
    <section class="verification">
      <img
        src="${qrDataUrl}"
        alt="Código QR de verificación"
      />

      <div>
        <span class="label">Verificación digital</span>
        <strong>Escanea para consultar la transferencia</strong>
        <p>
          ${escapeHtml(transfer.transfer_number)}
        </p>
      </div>
    </section>
  `
      : "";

    printWindow.document.write(`
    <!DOCTYPE html>
    <html lang="es">
      <head>
        <meta charset="UTF-8" />
        <title>Comprobante ${escapeHtml(
      transfer.transfer_number
    )}</title>

        <style>
          @page {
            size: A4;
            margin: 15mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            margin: 0;
            color: #1e293b;
            font-family: Arial, sans-serif;
          }

          .document-header {
            display: flex;
            justify-content: space-between;
            gap: 22px;

            padding-bottom: 18px;
            border-bottom: 3px solid #e6008d;
          }

          .company-info {
            display: flex;
            align-items: center;
            gap: 14px;
          }

          .company-logo,
          .company-logo-fallback {
            width: 68px;
            height: 68px;
            flex: 0 0 auto;

            border: 1px solid #e2e8f0;
            border-radius: 12px;

            background: #fff;
            object-fit: contain;
          }

          .company-logo-fallback {
            display: grid;
            place-items: center;

            background: #fdf2f8;
            color: #e6008d;

            font-size: 24px;
            font-weight: 900;
          }

          .company-name {
            margin: 0 0 4px;
            color: #1e293b;
            font-size: 18px;
          }

          .company-data {
            margin: 3px 0;
            color: #64748b;
            font-size: 11px;
            line-height: 1.4;
          }

          .document-meta {
            text-align: right;
          }

          .document-kicker {
            margin: 0 0 5px;
            color: #e6008d;
            font-size: 10px;
            font-weight: 900;
            letter-spacing: 1px;
            text-transform: uppercase;
          }

          .document-number {
            margin: 0 0 10px;
            color: #1e293b;
            font-size: 23px;
          }

          .status {
            display: inline-block;

            padding: 7px 11px;
            border-radius: 999px;

            background: #dcfce7;
            color: #15803d;

            font-size: 11px;
            font-weight: 800;
          }

          .details-grid {
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 12px;

            margin: 22px 0 12px;
          }

          .card {
            padding: 14px;

            border: 1px solid #e2e8f0;
            border-radius: 10px;

            background: #f8fafc;
          }

          .label {
            display: block;
            margin-bottom: 6px;

            color: #64748b;

            font-size: 9px;
            font-weight: 900;
            letter-spacing: 0.8px;
            text-transform: uppercase;
          }

          .route {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
          }

          .route-part {
            flex: 1;
          }

          .route-arrow {
            color: #e6008d;
            font-size: 22px;
            font-weight: 900;
          }

          .notes {
            margin-bottom: 20px;
          }

          .notes p {
            margin: 0;
            color: #475569;
            font-size: 12px;
            line-height: 1.5;
          }

          .section-heading {
            display: flex;
            align-items: flex-end;
            justify-content: space-between;

            margin-bottom: 10px;
          }

          .section-heading h2 {
            margin: 3px 0 0;
            font-size: 16px;
          }

          .section-heading strong {
            color: #047857;
            font-size: 12px;
          }

          table {
            width: 100%;
            border-collapse: collapse;
          }

          th {
            padding: 11px 12px;

            background: #e6008d;
            color: #fff;

            font-size: 11px;
            text-align: left;
          }

          td {
            padding: 12px;

            border-bottom: 1px solid #e2e8f0;

            color: #475569;
            font-size: 11px;
          }

          .quantity {
            text-align: right;
          }

          .audit {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;

            margin-top: 22px;
            padding-top: 18px;

            border-top: 1px solid #e2e8f0;
          }

          .audit strong {
            display: block;
            color: #334155;
            font-size: 12px;
          }

          .audit span {
            color: #64748b;
            font-size: 10px;
          }

          .footer {
            margin-top: 24px;
            padding-top: 12px;

            border-top: 1px solid #e2e8f0;

            color: #94a3b8;
            font-size: 9px;
            text-align: center;
          }

          .verification {
  display: flex;
  align-items: center;
  gap: 14px;

  margin-top: 22px;
  padding: 14px;

  border: 1px dashed #cbd5e1;
  border-radius: 12px;

  background: #f8fafc;
}

.verification img {
  width: 74px;
  height: 74px;

  border-radius: 7px;
  background: #fff;
}

.verification strong {
  display: block;

  margin: 3px 0;

  color: #334155;
  font-size: 12px;
}

.verification p {
  margin: 0;
  color: #64748b;
  font-size: 10px;
}
        </style>
      </head>

      <body>
        <header class="document-header">
          <section class="company-info">
            ${logoHtml}

            <div>
              <h1 class="company-name">
                ${escapeHtml(company.company_name)}
              </h1>

              ${company.nit
        ? `
                    <p class="company-data">
                      ${escapeHtml(
          company.document_type || "NIT"
        )}: ${escapeHtml(company.nit)}
                    </p>
                  `
        : ""
      }

              ${companyContact
        ? `
                    <p class="company-data">
                      ${companyContact}
                    </p>
                  `
        : ""
      }
            </div>
          </section>

          <section class="document-meta">
            <p class="document-kicker">
              Comprobante interno
            </p>

            <h2 class="document-number">
              ${escapeHtml(transfer.transfer_number)}
            </h2>

            <span class="status">
              ${escapeHtml(statusLabel(transfer.status))}
            </span>
          </section>
        </header>

        <section class="details-grid">
          <article class="card">
            <span class="label">
              Fecha de transferencia
            </span>

            <strong>
              ${escapeHtml(
        formatDate(transfer.transfer_date)
      )}
            </strong>
          </article>

          <article class="card route">
            <div class="route-part">
              <span class="label">Bodega origen</span>
              <strong>
                ${escapeHtml(
        transfer.source_warehouse_name
      )}
              </strong>
            </div>

            <span class="route-arrow">→</span>

            <div class="route-part">
              <span class="label">Bodega destino</span>
              <strong>
                ${escapeHtml(
        transfer.destination_warehouse_name
      )}
              </strong>
            </div>
          </article>
        </section>

        ${transfer.notes
        ? `
              <section class="card notes">
                <span class="label">Observaciones</span>
                <p>${escapeHtml(transfer.notes)}</p>
              </section>
            `
        : ""
      }

        <section>
          <div class="section-heading">
            <div>
              <span class="label">Detalle</span>
              <h2>Productos trasladados</h2>
            </div>

            <strong>
              ${formatQuantity(totalQuantity)} unidades
            </strong>
          </div>

          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Código</th>
                <th class="quantity">Cantidad</th>
              </tr>
            </thead>

            <tbody>
              ${itemRows}
            </tbody>
          </table>
        </section>

        <section class="audit">
          <article>
            <span>Transferencia creada por</span>
            <strong>
              ${escapeHtml(
        created_by?.name || "Usuario no disponible"
      )}
            </strong>
            <span>
              ${escapeHtml(
        formatDateTime(transfer.created_at)
      )}
            </span>
          </article>

          <article>
            <span>Transferencia completada por</span>
            <strong>
              ${escapeHtml(
        completed_by?.name || "Pendiente"
      )}
            </strong>
            <span>
              ${escapeHtml(
        transfer.completed_at
          ? formatDateTime(transfer.completed_at)
          : "Sin completar"
      )}
            </span>
          </article>
        </section>

        ${qrHtml}
        <footer class="footer">
          ${escapeHtml(
        transferReceipt.document_type
      )} · Generado por ${escapeHtml(
        transferReceipt.generated_by || "NetLedger"
      )}
        </footer>
      </body>
    </html>
  `);

    printWindow.document.close();
  };

  return (
    <div className="warehouse-transfers">
      <section className="transfers-heading">
        <div>
          <p className="transfers-kicker">Inventario</p>
          <h1>Transferencias entre bodegas</h1>
          <p>
            Mueve productos entre bodegas conservando lotes,
            vencimientos y costo.
          </p>
        </div>

        <button
          type="button"
          className="btn-transfer-refresh"
          onClick={loadData}
          disabled={loading}
        >
          {loading ? "Actualizando..." : "Actualizar"}
        </button>
      </section>

      {canManageTransfers ? (
        <section className="transfer-card">
          <div className="transfer-card-heading">
            <div>
              <h2>Nueva transferencia</h2>
              <p>
                La salida se realiza por FEFO desde la bodega de
                origen.
              </p>
            </div>

            {cart.length > 0 && (
              <button
                type="button"
                className="btn-transfer-clear"
                onClick={resetForm}
              >
                Limpiar
              </button>
            )}
          </div>

          <form onSubmit={saveTransfer}>
            <div className="transfer-form-grid">
              <label>
                Bodega de origen
                <select
                  name="source_warehouse_id"
                  value={form.source_warehouse_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">
                    Selecciona una bodega
                  </option>

                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id}
                      value={warehouse.id}
                    >
                      {warehouse.code} — {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Bodega de destino
                <select
                  name="destination_warehouse_id"
                  value={form.destination_warehouse_id}
                  onChange={handleFormChange}
                  required
                >
                  <option value="">
                    Selecciona una bodega
                  </option>

                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse.id}
                      value={warehouse.id}
                      disabled={
                        Number(warehouse.id) ===
                        Number(form.source_warehouse_id)
                      }
                    >
                      {warehouse.code} — {warehouse.name}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Fecha de transferencia
                <input
                  type="date"
                  name="transfer_date"
                  value={form.transfer_date}
                  onChange={handleFormChange}
                  required
                />
              </label>
            </div>

            <label className="transfer-notes">
              Observaciones
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleFormChange}
                rows="2"
                placeholder="Información adicional de la transferencia"
              />
            </label>

            <div className="transfer-product-picker">
              <select
                value={selectedProductId}
                onChange={(event) =>
                  setSelectedProductId(event.target.value)
                }
              >
                <option value="">
                  Selecciona un producto para transferir
                </option>

                {products.map((product) => (
                  <option
                    key={product.id}
                    value={product.id}
                  >
                    {product.sku} — {product.name}
                  </option>
                ))}
              </select>

              <button
                type="button"
                className="btn-transfer-add"
                onClick={addProduct}
              >
                Agregar producto
              </button>
            </div>

            <div className="transfer-table-wrapper">
              <table className="transfer-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Cantidad a transferir</th>
                    <th />
                  </tr>
                </thead>

                <tbody>
                  {cart.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="transfer-empty"
                      >
                        Agrega productos a la transferencia.
                      </td>
                    </tr>
                  ) : (
                    cart.map((item) => (
                      <tr key={item.product_id}>
                        <td>
                          <strong>{item.name}</strong>
                          <small>{item.sku}</small>
                        </td>

                        <td>
                          <input
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={item.quantity}
                            onChange={(event) =>
                              updateItemQuantity(
                                item.product_id,
                                event.target.value
                              )
                            }
                          />
                        </td>

                        <td>
                          <button
                            type="button"
                            className="btn-transfer-remove"
                            onClick={() =>
                              removeItem(item.product_id)
                            }
                          >
                            Quitar
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="transfer-total">
              Total de unidades:{" "}
              <strong>
                {formatQuantity(totalQuantity)}
              </strong>
            </div>

            <button
              type="submit"
              className="btn-transfer-save"
              disabled={saving || cart.length === 0}
            >
              {saving
                ? "Creando transferencia..."
                : "Crear transferencia"}
            </button>
          </form>
        </section>
      ) : (
        <div className="transfer-readonly-message">
          No tienes permiso para crear transferencias.
        </div>
      )}

      <section className="transfer-card">
        <div className="transfer-card-heading">
          <div>
            <h2>Transferencias registradas</h2>
            <p>
              Completa una transferencia para mover el inventario.
            </p>
          </div>
        </div>

        <div className="transfer-table-wrapper">
          <table className="transfer-history-table">
            <thead>
              <tr>
                <th>Transferencia</th>
                <th>Fecha</th>
                <th>Origen</th>
                <th>Destino</th>
                <th>Productos</th>
                <th>Cantidad</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="8"
                    className="transfer-empty"
                  >
                    Cargando transferencias...
                  </td>
                </tr>
              ) : transfers.length === 0 ? (
                <tr>
                  <td
                    colSpan="8"
                    className="transfer-empty"
                  >
                    No hay transferencias registradas.
                  </td>
                </tr>
              ) : (
                transfers.map((transfer) => (
                  <tr key={transfer.id}>
                    <td>
                      <strong>
                        {transfer.transfer_number}
                      </strong>
                    </td>

                    <td>
                      {formatDate(transfer.transfer_date)}
                    </td>

                    <td>
                      {transfer.source_warehouse_name}
                    </td>

                    <td>
                      {transfer.destination_warehouse_name}
                    </td>

                    <td>{transfer.total_items}</td>

                    <td>
                      {formatQuantity(
                        transfer.total_quantity
                      )}
                    </td>

                    <td>
                      <span
                        className={
                          `transfer-status ${transfer.status}`
                        }
                      >
                        {statusLabel(transfer.status)}
                      </span>
                    </td>

                    <td className="transfer-actions">
                      <button
                        type="button"
                        className="btn-transfer-detail"
                        onClick={() => openTransferDetail(transfer.id)}
                      >
                        <span>Ver detalle</span>
                        <span aria-hidden="true">→</span>
                      </button>
                      {canManageTransfers &&
                        transfer.status === "draft" && (
                          <>
                            <button
                              type="button"
                              className="btn-transfer-complete"
                              onClick={() =>
                                requestTransferAction(transfer, "complete")
                              }
                            >
                              Completar
                            </button>

                            <button
                              type="button"
                              className="btn-transfer-cancel"
                              onClick={() =>
                                requestTransferAction(transfer, "cancel")
                              }
                            >
                              Cancelar
                            </button>
                          </>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
      {confirmation && (
        <div
          className="transfer-confirmation-backdrop"
          onClick={closeConfirmation}
        >
          <section
            className="transfer-confirmation-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={
                confirmation.action === "complete"
                  ? "transfer-confirmation-icon complete"
                  : "transfer-confirmation-icon cancel"
              }
            >
              {confirmation.action === "complete" ? "✓" : "!"}
            </div>

            <h2>
              {confirmation.action === "complete"
                ? "¿Completar transferencia?"
                : "¿Cancelar transferencia?"}
            </h2>

            <p>
              Transferencia{" "}
              <strong>
                {confirmation.transfer.transfer_number}
              </strong>
            </p>

            <p className="transfer-confirmation-description">
              {confirmation.action === "complete"
                ? "Se descontará inventario de la bodega origen y se crearán los lotes equivalentes en la bodega destino. Esta acción no se puede editar después."
                : "La transferencia permanecerá registrada como cancelada y no moverá inventario."}
            </p>

            <div className="transfer-confirmation-details">
              <span>
                <strong>Origen:</strong>{" "}
                {
                  confirmation.transfer
                    .source_warehouse_name
                }
              </span>

              <span>
                <strong>Destino:</strong>{" "}
                {
                  confirmation.transfer
                    .destination_warehouse_name
                }
              </span>

              <span>
                <strong>Cantidad:</strong>{" "}
                {formatQuantity(
                  confirmation.transfer.total_quantity
                )}{" "}
                unidades
              </span>
            </div>

            <div className="transfer-confirmation-actions">
              <button
                type="button"
                className="btn-transfer-modal-close"
                onClick={closeConfirmation}
                disabled={Boolean(processingTransferId)}
              >
                Volver
              </button>

              <button
                type="button"
                className={
                  confirmation.action === "complete"
                    ? "btn-transfer-modal-complete"
                    : "btn-transfer-modal-cancel"
                }
                onClick={confirmTransferAction}
                disabled={Boolean(processingTransferId)}
              >
                {processingTransferId
                  ? "Procesando..."
                  : confirmation.action === "complete"
                    ? "Sí, completar"
                    : "Sí, cancelar"}
              </button>
            </div>
          </section>
        </div>
      )}
      {(loadingTransferDetail || transferDetail) && (
        <div
          className="transfer-confirmation-backdrop"
          onClick={() => setTransferDetail(null)}
        >
          <section
            className="transfer-detail-modal transfer-printable"
            onClick={(event) => event.stopPropagation()}
          >
            {loadingTransferDetail ? (
              <p>Cargando detalle de transferencia...</p>
            ) : (
              <>
                <div className="transfer-detail-header">
                  <div>
                    <p className="transfers-kicker">
                      Trazabilidad de transferencia
                    </p>

                    <h2>{transferDetail.transfer_number}</h2>

                    <span
                      className={`transfer-status ${transferDetail.status}`}
                    >
                      {statusLabel(transferDetail.status)}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn-transfer-detail-close"
                    onClick={() => {
                      setTransferDetail(null);
                      setTransferReceipt(null);
                    }}
                    aria-label="Cerrar detalle"
                  >
                    ×
                  </button>
                </div>
                {transferReceipt && (
                  <section className="transfer-receipt-preview">
                    {getLogoUrl(transferReceipt.company.logo) && (
                      <img
                        src={getLogoUrl(transferReceipt.company.logo)}
                        alt={`Logo de ${transferReceipt.company.company_name}`}
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    )}

                    <div>
                      <strong>
                        {transferReceipt.company.company_name}
                      </strong>

                      {transferReceipt.company.nit && (
                        <span>
                          NIT: {transferReceipt.company.nit}
                        </span>
                      )}

                      {transferReceipt.company.address && (
                        <span>
                          {transferReceipt.company.address}
                          {transferReceipt.company.city
                            ? ` · ${transferReceipt.company.city}`
                            : ""}
                        </span>
                      )}
                    </div>

                    <div className="transfer-receipt-audit">
                      <span>
                        Creada por:{" "}
                        <strong>
                          {transferReceipt.created_by?.name ||
                            "Usuario no disponible"}
                        </strong>
                      </span>

                      {transferReceipt.completed_by?.name && (
                        <span>
                          Completada por:{" "}
                          <strong>
                            {transferReceipt.completed_by.name}
                          </strong>
                        </span>
                      )}
                    </div>
                  </section>
                )}

                <section className="transfer-detail-overview">
                  <article>
                    <span>Fecha de transferencia</span>
                    <strong>
                      {formatDate(transferDetail.transfer_date)}
                    </strong>
                  </article>

                  <article className="transfer-detail-route">
                    <div>
                      <span>Origen</span>
                      <strong>
                        {transferDetail.source_warehouse_name}
                      </strong>
                    </div>

                    <b aria-hidden="true">→</b>

                    <div>
                      <span>Destino</span>
                      <strong>
                        {transferDetail.destination_warehouse_name}
                      </strong>
                    </div>
                  </article>
                </section>

                {transferDetail.notes && (
                  <section className="transfer-detail-notes">
                    <span>Observaciones</span>
                    <p>{transferDetail.notes}</p>
                  </section>
                )}

                <section className="transfer-detail-items">
                  <div className="transfer-detail-items-heading">
                    <div>
                      <span>Productos trasladados</span>
                      <h3>
                        {transferDetail.items.length} producto
                        {transferDetail.items.length !== 1 ? "s" : ""}
                      </h3>
                    </div>

                    <strong>
                      {formatQuantity(
                        transferDetail.items.reduce(
                          (total, item) =>
                            total + Number(item.quantity || 0),
                          0
                        )
                      )}{" "}
                      unidades
                    </strong>
                  </div>

                  <div className="transfer-table-wrapper">
                    <table className="transfer-history-table">
                      <thead>
                        <tr>
                          <th>Producto</th>
                          <th>Código</th>
                          <th className="transfer-quantity-column">
                            Cantidad trasladada
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {transferDetail.items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <strong>{item.product_name}</strong>
                            </td>
                            <td>{item.product_sku}</td>
                            <td className="transfer-quantity-column">
                              {formatQuantity(item.quantity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>

                <div className="transfer-detail-actions">
                  <button
                    type="button"
                    className="btn-transfer-detail-print"
                    onClick={printTransferDetail}
                  >
                    Imprimir comprobante
                  </button>

                  <button
                    type="button"
                    className="btn-transfer-detail-done"
                    onClick={() => {
                      setTransferDetail(null);
                      setTransferReceipt(null);
                    }}
                  >
                    Listo
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

export default CompanyWarehouseTransfers;